/**
 * Human Design — the reading layer under each individual activation.
 *
 * `hdStructure.ts` answers questions about the chart as a whole (circuits,
 * islands, centres). This file answers them one planet at a time: what a
 * planet MEANS in a chart, what a line position means wherever it lands, which
 * quarter of the mandala a gate sits in, whether a channel is conscious or
 * unconscious, and what today's sky is temporarily doing to the chart.
 *
 * Same split as everywhere else on this page: the derivations are arithmetic
 * on the wheel and the 36 channels, the sentences are labelled as reading.
 */

import { GATE_ORDER, CHANNELS, GATE_CENTER } from './humandesign';

// ---------------------------------------------------------- the quarters

export type QuarterId = 'initiation' | 'civilization' | 'duality' | 'mutation';

export interface Quarter {
  id: QuarterId; th: string; en: string;
  themeTh: string; themeEn: string;
  colour: string;
}

export const QUARTERS: Record<QuarterId, Quarter> = {
  initiation: {
    id: 'initiation', th: 'จตุภาคแห่งการเริ่มต้น', en: 'Quarter of Initiation', colour: '#a78bfa',
    themeTh: 'จุดหมายสำเร็จผ่าน "จิตใจ" — งานของคุณคือคิด ตั้งคำถาม และจุดประกายให้คนอื่น',
    themeEn: 'Purpose fulfilled through MIND — thinking, questioning, sparking others.',
  },
  civilization: {
    id: 'civilization', th: 'จตุภาคแห่งอารยธรรม', en: 'Quarter of Civilization', colour: '#38bdf8',
    themeTh: 'จุดหมายสำเร็จผ่าน "รูปธรรม" — สร้างสิ่งที่จับต้องได้ ระบบ ผลงาน โครงสร้าง',
    themeEn: 'Purpose fulfilled through FORM — building things that last: systems, work, structure.',
  },
  duality: {
    id: 'duality', th: 'จตุภาคแห่งทวิภาวะ', en: 'Quarter of Duality', colour: '#f0abfc',
    themeTh: 'จุดหมายสำเร็จผ่าน "ความสัมพันธ์" — สิ่งสำคัญมาถึงคุณผ่านคนอีกคน',
    themeEn: 'Purpose fulfilled through BONDING — what matters arrives through another person.',
  },
  mutation: {
    id: 'mutation', th: 'จตุภาคแห่งการกลายพันธุ์', en: 'Quarter of Mutation', colour: '#4ade80',
    themeTh: 'จุดหมายสำเร็จผ่าน "การเปลี่ยนแปลง" — คุณอยู่ที่นี่เพื่อทำให้บางอย่างไม่เหมือนเดิม',
    themeEn: 'Purpose fulfilled through TRANSFORMATION — you are here to make something different.',
  },
};

/**
 * Each quarter is a 90° slice of the wheel: sixteen consecutive gates. The
 * slices are taken from GATE_ORDER rather than typed out, so they cannot drift
 * from the wheel the chart is actually computed on — and the Mutation quarter
 * wraps past the wheel's start (41, 19 sit at the end of its run), which is
 * exactly the kind of detail a hand-typed table gets wrong.
 */
const QUARTER_ORDER: QuarterId[] = ['initiation', 'civilization', 'duality', 'mutation'];
const QUARTER_OF_GATE: Record<number, QuarterId> = (() => {
  const out: Record<number, QuarterId> = {};
  // The wheel starts two gates (41, 19) into the Mutation quarter.
  const OFFSET = 2;
  for (let i = 0; i < 64; i++) {
    const gate = GATE_ORDER[(i + OFFSET) % 64];
    out[gate] = QUARTER_ORDER[Math.floor(i / 16)];
  }
  return out;
})();

export const quarterOfGate = (gate: number): Quarter => QUARTERS[QUARTER_OF_GATE[gate]];

/** Sixteen gates per quarter, sixty-four in total, or the slicing is wrong. */
export function assertQuarters(): string[] {
  const tally: Record<string, number> = {};
  for (let g = 1; g <= 64; g++) {
    const q = QUARTER_OF_GATE[g];
    if (!q) return [`gate ${g} has no quarter`];
    tally[q] = (tally[q] || 0) + 1;
  }
  return Object.entries(tally).filter(([, n]) => n !== 16).map(([q, n]) => `${q} has ${n} gates, expected 16`);
}

// ------------------------------------------------------------ the lines

export interface LineRead { n: number; th: string; en: string; readTh: string; readEn: string; marketTh: string; marketEn: string; }

/**
 * The six lines mean the same thing wherever they land — in the profile, and
 * in each of the twenty-six activations. Printing the keynote next to every
 * gate.line is what turns the activation table from coordinates into a read.
 */
export const LINES: LineRead[] = [
  { n: 1, th: 'ผู้ตรวจสอบ', en: 'Investigator', readTh: 'ต้องรู้ให้ลึกก่อนถึงจะมั่นใจ — ความปลอดภัยมาจากฐานข้อมูลที่แน่น', readEn: 'Needs depth before confidence — security comes from a solid foundation.', marketTh: 'อย่าเข้าไม้ก่อนอ่านจบ ความไม่มั่นใจของคุณคือสัญญาณว่ายังค้นไม่พอ', marketEn: 'Do not enter before the research is done — your unease means the homework is incomplete.' },
  { n: 2, th: 'ผู้สันโดษ', en: 'Hermit', readTh: 'มีพรสวรรค์ที่ทำได้เองโดยไม่รู้ตัว แต่ต้องถูก "เรียก" ออกมา', readEn: 'A natural talent that works without effort — but it has to be called out.', marketTh: 'สไตล์ที่ใช่ของคุณคือสิ่งที่ทำได้เองอยู่แล้ว ไม่ใช่สิ่งที่ต้องฝืนเรียน', marketEn: 'Your edge is the thing you already do effortlessly, not the thing you force yourself to learn.' },
  { n: 3, th: 'ผู้ทดลอง', en: 'Martyr', readTh: 'เรียนรู้จากการลองแล้วพลาด — ความผิดพลาดคือข้อมูล ไม่ใช่ความล้มเหลว', readEn: 'Learns by trial and error — a mistake is data, not failure.', marketTh: 'ไม้ที่เสียคือค่าเล่าเรียน จดไว้เป็นระบบ แล้วมันจะกลายเป็นความได้เปรียบ', marketEn: 'Losing trades are tuition — log them systematically and they become the edge.' },
  { n: 4, th: 'ผู้เชื่อมโยง', en: 'Opportunist', readTh: 'โอกาสมาจากเครือข่ายที่มีอยู่แล้ว ไม่ใช่จากคนแปลกหน้า', readEn: 'Opportunity comes through the existing network, not from strangers.', marketTh: 'ดีลและข้อมูลดี ๆ มาจากคนที่รู้จักอยู่แล้ว — รักษาคอนเนกชันไว้', marketEn: 'The good information arrives through people you already know — keep the network warm.' },
  { n: 5, th: 'ผู้ชี้นำ', en: 'Heretic', readTh: 'คนคาดหวังกับคุณเกินจริง — ให้คำตอบที่ใช้ได้จริงเท่านั้น', readEn: 'People project expectations onto you — offer only what actually works.', marketTh: 'ระวังการถูกยกให้เป็น "เซียน" แล้วต้องรักษาภาพ จนถือไม้ที่ควรตัดไปนานแล้ว', marketEn: 'Beware being cast as the expert and then holding a position to protect the image.' },
  { n: 6, th: 'ผู้เป็นแบบอย่าง', en: 'Role Model', readTh: 'ชีวิตแบ่งสามช่วง — ลองผิดลองถูก ถอยมามอง แล้วกลับมาเป็นแบบอย่าง', readEn: 'Life in three phases — trial, withdrawal to observe, then return as an example.', marketTh: 'มุมมองระยะยาวคือจุดแข็ง อย่าเอาตัวเองไปวัดกับคนที่เทรดรายวัน', marketEn: 'The long view is the strength — do not measure yourself against day traders.' },
];

export const lineRead = (n: number): LineRead => LINES[Math.min(6, Math.max(1, n)) - 1];

// ---------------------------------------------------------- the planets

export interface PlanetRead { k: string; th: string; en: string; weightTh?: string; readTh: string; readEn: string; }

/**
 * What each planet is doing in a chart. The Sun/Earth pair carries the great
 * majority of the imprint, which is why the Incarnation Cross is built from it
 * and why the other eleven are context rather than headline.
 */
export const PLANETS: PlanetRead[] = [
  { k: 'sun', th: 'ดวงอาทิตย์', en: 'Sun', weightTh: '~70% ของพลังงานทั้งชาร์ต', readTh: 'สิ่งที่คุณมาเพื่อแสดงออก — แก่นของตัวตน', readEn: 'What you are here to express — the core of the imprint.' },
  { k: 'earth', th: 'โลก', en: 'Earth', readTh: 'สิ่งที่ทำให้คุณยืนอยู่ได้ — ฐานที่ถ่วงดวงอาทิตย์ไว้', readEn: 'What keeps you grounded — the ballast for the Sun.' },
  { k: 'nnode', th: 'นอร์ทโหนด', en: 'North Node', readTh: 'ทิศทางและสภาพแวดล้อมของชีวิตครึ่งหลัง (หลังราว 40 ปี)', readEn: 'The direction and environment of the second half of life (after roughly 40).' },
  { k: 'snode', th: 'เซาท์โหนด', en: 'South Node', readTh: 'สภาพแวดล้อมของชีวิตครึ่งแรก — ที่ที่คุณมาจาก', readEn: 'The environment of the first half — where you are coming from.' },
  { k: 'moon', th: 'ดวงจันทร์', en: 'Moon', readTh: 'แรงขับที่ทำให้ลงมือ — สิ่งที่ผลักให้คุณไม่หยุดนิ่ง', readEn: 'The driving force — what keeps you in motion.' },
  { k: 'mercury', th: 'พุธ', en: 'Mercury', readTh: 'สิ่งที่คุณต้องได้พูด ได้สื่อสาร ไม่งั้นจะอึดอัด', readEn: 'What you need to communicate, or it festers.' },
  { k: 'venus', th: 'ศุกร์', en: 'Venus', readTh: 'คุณค่าและศีลธรรม — สิ่งที่คุณตัดสินว่า "ถูก"', readEn: 'Values and morality — what you judge as right.' },
  { k: 'mars', th: 'อังคาร', en: 'Mars', readTh: 'ความไม่สุกงอม — พลังงานที่เอาแต่ใจตอนหนุ่มสาวและสุกช้าที่สุด', readEn: 'Immaturity — the energy that runs hot early and matures last.' },
  { k: 'jupiter', th: 'พฤหัส', en: 'Jupiter', readTh: 'กฎและรางวัล — สิ่งที่ให้ผลตอบแทนเมื่อคุณใช้ชีวิตตรงกับตัวเอง', readEn: 'Law and reward — what pays off when you live correctly for your design.' },
  { k: 'saturn', th: 'เสาร์', en: 'Saturn', readTh: 'วินัยและราคาที่ต้องจ่าย — ที่ที่ชีวิตจะดัดคุณจนกว่าจะยอม', readEn: 'Discipline and the price — where life keeps correcting you until you comply.' },
  { k: 'uranus', th: 'ยูเรนัส', en: 'Uranus', readTh: 'ความไม่แน่นอน — ด้านที่คาดเดาไม่ได้แม้แต่ตัวคุณเอง', readEn: 'Chaos — the part not even you can predict.' },
  { k: 'neptune', th: 'เนปจูน', en: 'Neptune', readTh: 'ภาพลวงและสิ่งที่ยังมองไม่เห็น — จุดที่คุณหลอกตัวเองได้ง่ายที่สุด', readEn: 'Illusion and the not-yet-visible — where self-deception is easiest.' },
  { k: 'pluto', th: 'พลูโต', en: 'Pluto', readTh: 'ความจริงที่หลีกไม่ได้ — สิ่งที่จะถูกรื้อจนเหลือแต่ของแท้', readEn: 'Unavoidable truth — what gets stripped until only the real thing is left.' },
];

export const PLANET_BY_KEY = new Map(PLANETS.map(p => [p.k, p]));

// ------------------------------------------------- conscious / unconscious

export type Consciousness = 'conscious' | 'unconscious' | 'mixed';

/**
 * A channel completed by two Personality gates is something the reader knows
 * about themselves; one completed by two Design gates runs the body without
 * ever reaching awareness; a mixed one is half known. Different experience,
 * same line in a list — so it gets labelled.
 */
export function channelConsciousness(a: number, b: number, persGates: number[], desGates: number[]): Consciousness {
  const P = new Set(persGates), D = new Set(desGates);
  const aP = P.has(a), bP = P.has(b), aD = D.has(a), bD = D.has(b);
  if (aP && bP && !(aD && bD)) return 'conscious';
  if (aD && bD && !(aP && bP)) return 'unconscious';
  return 'mixed';
}

// ------------------------------------------------------------- transits

export interface TransitChannel { gates: [number, number]; mine: number; sky: number; centres: [string, string]; }

export interface TransitRead {
  /** Channels the sky completes for you today, using a gate you already hold. */
  channels: TransitChannel[];
  /** Centres that are defined today but are not in your natal chart. */
  newCentres: string[];
  /** Gates the sky is switching on that you already have (doubled up, not new). */
  echoed: number[];
}

/**
 * What today's sky is temporarily doing to this chart.
 *
 * Transits are the one part of Human Design that changes daily, and the reason
 * the same person trades differently on two identical setups. A hanging gate
 * is half a channel; when the sky lights the other half, that channel is
 * complete for as long as the transit lasts — which feels like a capacity that
 * is suddenly, and temporarily, available.
 */
export function readTransit(skyGates: number[], activeGates: number[], definedCentres: string[]): TransitRead {
  const mine = new Set(activeGates);
  const sky = new Set(skyGates);
  const defined = new Set(definedCentres);

  const channels: TransitChannel[] = [];
  for (const [a, b] of CHANNELS) {
    const aMine = mine.has(a), bMine = mine.has(b);
    if (aMine && bMine) continue;               // already yours — not a transit gift
    if (aMine && sky.has(b)) channels.push({ gates: [a, b], mine: a, sky: b, centres: [GATE_CENTER[a], GATE_CENTER[b]] });
    else if (bMine && sky.has(a)) channels.push({ gates: [a, b], mine: b, sky: a, centres: [GATE_CENTER[b], GATE_CENTER[a]] });
  }

  const newCentres = [...new Set(channels.flatMap(c => c.centres))].filter(c => !defined.has(c));
  const echoed = activeGates.filter(g => sky.has(g));

  return { channels, newCentres, echoed };
}
